import { useQuery } from 'react-query'

import { usePagination } from '@webapp/core/hooks/usePagination'
import { type ListExportTasksParams } from '@webapp/modules/reports/business-logic/interfaces/IReportExportTaskRepository'
import { type ExportTaskType } from '@webapp/modules/reports/entities/enums/ExportTaskTypeEnum'
import { ReportExportTask } from '@webapp/modules/reports/entities/ReportExportTask'
import { exportTaskQueries } from '@webapp/modules/reports/query-configs/exportTaskQueries'

export interface UseQueryExportTasksParams {
  /** Task-type codes derived from the referrer by the FS (BHV-035) */
  typeFilter: ExportTaskType[]
  /** FS-owned date window (default last 3 months → tomorrow, BHV-035) */
  dateRange: { from: string; to: string }
}

// Combines the FS-owned type/date filters with the pagination store's
// sort/page state (PaginationID REPORT_EXPORT_HISTORY, mode url, default
// -created_at). delete-export-task invalidates the export-task base key,
// which refetches this active query (row disappears — BHV-037).
export const useQueryExportTasks = ({
  typeFilter,
  dateRange,
}: UseQueryExportTasksParams) => {
  const { params, updateNumPages } = usePagination({
    id: 'REPORT_EXPORT_HISTORY',
  })

  const listParams: ListExportTasksParams = {
    types: typeFilter,
    createdFrom: dateRange.from,
    createdTo: dateRange.to,
    orderBy: params.orderBy ?? '-created_at',
    page: params.page,
    pageSize: params.pageSize,
  }

  const { data, isFetching } = useQuery({
    ...exportTaskQueries.list(listParams),
    onSuccess: (res) => {
      updateNumPages(res.pagination.numPages)
    },
  })

  return {
    items: (data?.items ?? []).map(
      (constructor) => new ReportExportTask(constructor)
    ),
    pagination: data?.pagination,
    isFetching,
  }
}
